import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AgreementViewModal, useAgreementsStore } from "@/features/agreements";
import {
  CONSENT_REOPEN_EVENT,
  hasConsentDecision,
  setConsent,
} from "@/lib/consent";

/**
 * Consent Mode v2 bannerı.
 *
 * Varsayılan "denied"; kullanıcı cevaplayana kadar reklam ve analitik
 * çerezleri yazılmıyor. Cevap `setConsent` ile hem saklanıyor hem de
 * `gtag('consent','update')` ile GTM'e iletiliyor — bekleyen tag'ler ancak
 * o çağrıyla serbest kalıyor.
 */
export const ConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const { openViewModal } = useAgreementsStore();

  // İlk render'da karar veriliyor: karar verilmişse banner hiç mount olmuyor.
  const [isVisible, setIsVisible] = React.useState(() => !hasConsentDecision());

  // Ayarlardan "çerez tercihleri" seçildiğinde banner geri geliyor.
  React.useEffect(() => {
    const reopen = () => setIsVisible(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  const answer = (granted: boolean) => {
    setConsent(granted);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        // z-[900]: modal ve toast katmanlarının altında kalıyor; bir ödeme
        // ekranının üstüne binmesin.
        // pointer-events-none: sarmalayıcı inset-x-0 olduğu için sidebar
        // profili gibi alt köşedeki tıklamaları yutmasın. Kartın kendisi
        // aşağıda pointer-events-auto ile tıklanabilir kalıyor.
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[900] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
        role="region"
        aria-label={t("consent.title")}
      >
        <div className="pointer-events-auto mx-auto flex w-full max-w-3xl flex-col gap-3 rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-lg dark:border-border-secondary dark:bg-bg-secondary sm:flex-row sm:items-center sm:gap-4 sm:p-5">
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
              {t("consent.title")}
            </p>
            <p className="mt-1 text-xs leading-5 text-text-tertiary dark:text-text-tertiary">
              {t("consent.description")}{" "}
              <button
                type="button"
                onClick={() => openViewModal("privacy-policy")}
                className="font-medium text-text-primary underline underline-offset-2 hover:opacity-80 dark:text-text-primary"
              >
                {t("auth.privacyPolicy")}
              </button>
            </p>
          </div>

          {/* Mobilde alt alta ve tam genişlik; dar ekranda yan yana iki buton
              sığmıyor ve metin kırpılıyor. */}
          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
            <Button
              variant="outline"
              size="sm"
              onClick={() => answer(false)}
              // `outline` varyantı metin rengi tanımlamıyor, miras alıyor;
              // koyu temada koyu zemin üstünde koyu yazı çıkıyordu. Rengi
              // burada sabitliyoruz — varyantı değiştirmek diğer tüm
              // kullanımları da etkilerdi.
              className="w-full text-text-primary dark:text-text-primary sm:w-auto"
            >
              {t("consent.reject")}
            </Button>
            <Button
              variant="darkPrimary"
              size="sm"
              onClick={() => answer(true)}
              className="w-full sm:w-auto"
            >
              {t("consent.accept")}
            </Button>
          </div>
        </div>
      </div>

      {/* Gizlilik metni bannerdan da açılabilmeli; modal store ile sürülüyor. */}
      <AgreementViewModal />
    </>
  );
};
